import Layout from "@/components/layout/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Shield, Search, CreditCard, CheckCircle, AlertTriangle, Battery, Zap, BookOpen } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";

const sections = [
  {
    icon: Search,
    title: "1. Define tus necesidades",
    content: [
      "¿Para qué lo vas a usar? Desplazamientos urbanos, ocio, o trayectos largos.",
      "¿Cuántos kilómetros al día? Un patinete con 25 km de autonomía es suficiente para la mayoría.",
      "¿Necesitas portabilidad? Si lo llevas en transporte público, elige uno plegable y ligero (<15 kg).",
    ],
  },
  {
    icon: Battery,
    title: "2. Características clave",
    content: [
      "Autonomía: Mínimo 20 km para uso urbano. Los modelos premium llegan a 50-70 km.",
      "Motor: 250W para superficies planas, 500W+ si hay cuestas en tu ruta.",
      "Neumáticos: Los hinchables absorben mejor los baches; los macizos no se pinchan.",
      "Frenos: Disco mecánico o hidráulico. Evita los que solo tienen freno eléctrico.",
      "Peso máximo soportado: Comprueba que soporta tu peso + mochila/compras.",
    ],
  },
  {
    icon: Shield,
    title: "3. Homologación DGT",
    content: [
      "Desde 2024, todos los VMP deben tener certificado de circulación de la DGT.",
      "Sin homologación NO puedes circular legalmente por vías públicas.",
      "El certificado garantiza que cumple velocidad máxima (25 km/h), luces y reflectantes.",
      "Consulta la lista actualizada de vehículos homologados en la web de la DGT.",
    ],
  },
  {
    icon: CreditCard,
    title: "4. Presupuesto orientativo",
    content: [
      "Gama baja (150-300€): Para uso ocasional y distancias cortas.",
      "Gama media (300-600€): Mejor autonomía, frenos de disco, neumáticos de calidad.",
      "Gama alta (600-1500€): Doble suspensión, gran autonomía, motor potente.",
      "Premium (+1500€): Patinetes de alto rendimiento para entusiastas.",
    ],
  },
  {
    icon: CheckCircle,
    title: "5. Nuevo vs. Segunda mano",
    content: [
      "Nuevo: Garantía completa (2 años), batería en perfecto estado, última tecnología.",
      "Segunda mano: Ahorra un 30-50%, pero verifica el estado de la batería (ciclos de carga).",
      "Pide siempre un vídeo del patinete funcionando antes de comprarlo de segunda mano.",
      "Revisa las ruedas, frenos y que cargue correctamente al 100%.",
    ],
  },
  {
    icon: AlertTriangle,
    title: "6. Señales de alerta al comprar",
    content: [
      "Precio muy por debajo del mercado → posible estafa o producto robado.",
      "Vendedor que no permite ver el patinete en persona.",
      "Sin factura de compra original ni certificado DGT.",
      "Batería que no carga al 100% → puede necesitar reemplazo (coste alto).",
      "Ruidos extraños en el motor o ruedas.",
    ],
  },
];

const faqs = [
  {
    q: "¿Es legal comprar un patinete eléctrico de segunda mano sin homologación DGT?",
    a: "Puedes comprarlo, pero desde 2024 no podrás circular legalmente por vías públicas en España sin el certificado de homologación DGT. Antes de comprar, asegúrate de que el modelo aparece en la lista oficial de vehículos certificados.",
  },
  {
    q: "¿Cómo sé si la batería de un patinete de segunda mano está en buen estado?",
    a: "Pide al vendedor el número de ciclos de carga, comprueba que cargue al 100 % y prueba la autonomía real. Una batería degradada puede perder hasta el 40 % de capacidad. Evita patinetes con más de 500 ciclos si no se ha cambiado la batería.",
  },
  {
    q: "¿Cuánto puedo ahorrar comprando un patinete eléctrico de segunda mano?",
    a: "Habitualmente entre un 30 % y un 50 % respecto al precio nuevo, dependiendo del modelo, edad y estado. Los modelos premium con poco uso ofrecen el mejor ratio calidad/precio.",
  },
  {
    q: "¿Qué documentación debo pedir al comprar un patinete eléctrico usado?",
    a: "Solicita la factura original, el certificado de homologación DGT (obligatorio desde 2024) y, si es posible, el manual y los accesorios. Esto facilita la matriculación y la reventa futura.",
  },
  {
    q: "¿Es seguro pagar online un patinete de segunda mano?",
    a: "Sí, siempre que uses una plataforma con pago protegido como la nuestra: el dinero se retiene hasta que recibes y verificas el patinete. Evita transferencias directas entre particulares sin intermediario.",
  },
];

const BuyingGuide = () => {
  useSEO({
    title: "Guía de compra de patinetes eléctricos 2025",
    description: "Guía completa para comprar tu patinete eléctrico: autonomía, motor, frenos, homologación DGT, presupuesto y consejos de seguridad. Todo lo que necesitas antes de comprar.",
    canonical: "/guia-compra",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: "Guía de compra de patinetes eléctricos",
        author: { "@type": "Organization", name: "Patinete Eléctrico Segunda Mano" },
        inLanguage: "es-ES",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Inicio", item: "https://patineteelectricosegundamano.com/" },
          { "@type": "ListItem", position: 2, name: "Guía de compra", item: "https://patineteelectricosegundamano.com/guia-compra" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  });
  return (
  <Layout>
    <section className="py-10">
      <div className="container max-w-3xl">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen className="h-7 w-7 text-primary" />
          <h1 className="font-display text-3xl font-bold">Guía de compra de patinetes eléctricos</h1>
        </div>
        <p className="text-muted-foreground mb-8">Todo lo que necesitas saber antes de comprar tu patinete eléctrico</p>

        <div className="space-y-6">
          {sections.map((section, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                    <section.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h2 className="font-display text-lg font-bold">{section.title}</h2>
                </div>
                <ul className="space-y-2">
                  {section.content.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-12">
          <h2 className="font-display text-2xl font-bold mb-4">Preguntas frecuentes</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="group rounded-xl border border-border bg-card p-5 open:shadow-md transition-shadow">
                <summary className="cursor-pointer list-none font-semibold flex items-center justify-between gap-4">
                  <span>{f.q}</span>
                  <span className="shrink-0 text-primary transition-transform group-open:rotate-45 text-xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>

        <div className="mt-10 rounded-xl border border-border bg-card p-8 text-center">
          <Zap className="h-10 w-10 mx-auto text-primary" />
          <h3 className="mt-4 font-display text-xl font-bold">¿Listo para encontrar tu patinete?</h3>
          <p className="mt-2 text-sm text-muted-foreground">Explora los anuncios disponibles o compara modelos</p>
          <div className="mt-4 flex justify-center gap-3">
            <Button asChild><Link to="/buscar">Explorar patinetes</Link></Button>
            <Button variant="outline" asChild><Link to="/comparador">Comparar modelos</Link></Button>
          </div>
        </div>
      </div>
    </section>
  </Layout>
  );
};

export default BuyingGuide;
